import React from 'react';
import { Button } from 'antd';
import { interpolateString } from '@utils/string';
import usePipelineActions from '@hooks/usePipelineActions';
import { COMMON_FORM_ERRORS } from '@constants/common';
import { StatusCodes } from '@constants/enum/common';
import { LOGS_LINK, PIPELINE_STATUS_MESSAGES } from '@constants/pipeline-details';
import {
  CANCEL_BUTTON_LABEL,
  DEFINE_PIPELINE_MODAL_TITLE,
  DELETE_BUTTON_LABEL,
  NAME_NEW_PIPELINE_MODAL_OK_BUTTON,
  NAME_NEW_PIPELINE_MODAL_TITLE,
  PipelineStatusCodes,
  RENAME_PIPELINE_MODAL_OK_BUTTON,
  RENAME_PIPELINE_MODAL_TITLE,
  UNDEPLOY_BUTTON_LABEL,
} from '@constants/pipelines';
import { IPipeline } from '@redux/types/types';
import NamePipelineModal from '@components/namePipelineModal/NamePipelineModal';
import PipelineConfirmationModal from '@components/pipelines/pipelineConfirmationModal/PipelineConfirmationModal';
import RenderEmptyPipelineState from '@components/RenderEmptyPipelineState/RenderEmptyPipelineState';
import RenderPipelinesListTable from '@components/RenderPipelinesListTable/RenderPipelinesListTable';
import ShareModal from '@components/shareModal/ShareModal';
import useFreeLimits from '@modules/Upgrade/hooks/useFreeLimits';
import { useColumns } from '@pages/pipelines/hooks/useColumns';
import usePipelines from '@pages/pipelines/hooks/usePipelines';
import usePipelinesList from '@pages/pipelines/hooks/usePipelinesList';
import styles from './pipelinesList.module.scss';

const PipelinesList = () => {
  const {
    status,
    deployPipeline,
    activatePipeline,
    actionStatus,
    activatePipelineActionStatus,
    message,
    deletePipeline,
    updatePipeline,
    renamePipelineNameStatus,
    pipelineCreateUpdatePipelineStatus,
    resetRenamePipelineNameStatus,
    resetMessage,
    newPipelineName,
    resetNewPipelineName,
  } = usePipelineActions();

  const {
    pipelinesList,
    totalPipelines,
    getPipelinesList,
    totalPipelinesInTheWorkspace,
    startPollingPipelinesList,
    stopPollingPipelinesList,
    activeTabPipelinesLandingPage,
    fetchPipelinesListStatus,
    sortValuePipelinesList,
    pipelineListDataParams,
  } = usePipelines();

  const {
    onChangeTab,
    handleMoreActionClick,
    pipelineToShare,
    setPipelineToShare,
    createPipelineFromEmptyYaml,
    setPipelineToDelete,
    pipelineToUndeploy,
    totalSelectedPipelines,
    setTotalSelectedPipelines,
    openPipelineConfirmationModal,
    getOnOkPipelineConfirmationModal,
    getOnCancelPipelineConfirmationModal,
    getPipelinesForConfirmationModal,
    onUndeployPipeline,
    pipelineToRename,
    showRenamePipelineModal,
    onAfterPipelineCreated,
    onAfterPipelineRenamed,
    onCloseNamePipelineModal,
    onGotToLogs,
    handleMenuCreatePipelineClick,
    onDeleteMultiplePipelines,
    createPipelineFromEmptyYamlHandler,
    isPipelineUnhealthy,
    resetCurrentPage,
    getEmptyLabelForTable,
  } = usePipelinesList();

  const { isLimitedUser } = useFreeLimits();

  const loading = [
    status,
    actionStatus,
    fetchPipelinesListStatus,
    activatePipelineActionStatus,
  ].includes(StatusCodes.IN_PROGRESS);

  const hasActiveFilters = !!pipelineListDataParams.current?.searchValue;
  const hasNoPipelines =
    fetchPipelinesListStatus === StatusCodes.SUCCESS &&
    // can we just use one of (pipelinesList) or (totalPipelinesInTheWorkspace)? and not both?
    pipelinesList.length === 0 &&
    totalPipelinesInTheWorkspace === 0 &&
    !activeTabPipelinesLandingPage;

  const isEmptyPipelineList = !loading && hasNoPipelines && !hasActiveFilters;

  // todo: this needs to be refactored, I can't do that yet because it's outside the scope of this PR
  const getUnhealthyMessage = (pipeline: IPipeline) => {
    const { name } = pipeline;
    const pipelineStatusMessage = PIPELINE_STATUS_MESSAGES.find((statusMessage) => {
      return statusMessage.status === PipelineStatusCodes.DEPLOYED;
    });

    if (pipelineStatusMessage && isPipelineUnhealthy(pipeline)) {
      const mainMessage = `${pipelineStatusMessage.title}. ${pipelineStatusMessage.message}`;
      const description = interpolateString(mainMessage, {
        logs: (
          <Button
            type="link"
            className={styles.pipelineStatus_alertLink}
            onClick={() => onGotToLogs(name)}
          >
            {LOGS_LINK}
          </Button>
        ),
      });
      return description;
    }
    return '';
  };

  // todo: currently moved useColumns out of this component to make it easier to debug.
  // we shouldn't need this much props
  const { columns } = useColumns({
    isPipelineUnhealthy,
    getUnhealthyMessage,
    deployPipeline,
    activatePipeline,
    setPipelineToShare,
    onUndeployPipeline,
    handleMoreActionClick,
    deletePipeline,
    setPipelineToDelete,
  });

  const getNamePipelineModalTitle = () => {
    if (createPipelineFromEmptyYaml) return DEFINE_PIPELINE_MODAL_TITLE;
    if (createPipelineFromEmptyYaml) return NAME_NEW_PIPELINE_MODAL_TITLE;
    return RENAME_PIPELINE_MODAL_TITLE;
  };

  return (
    <div className={styles.container}>
      {isEmptyPipelineList ? (
        <RenderEmptyPipelineState
          handleMenuCreatePipelineClick={handleMenuCreatePipelineClick}
          totalPipelines={totalPipelines}
          isLimitedUser={isLimitedUser}
        />
      ) : (
        <RenderPipelinesListTable
          activeTabPipelinesLandingPage={activeTabPipelinesLandingPage}
          onChangeTab={onChangeTab}
          isLimitedUser={isLimitedUser}
          totalPipelines={totalPipelines}
          handleMenuCreatePipelineClick={handleMenuCreatePipelineClick}
          fetchPipelinesListStatus={fetchPipelinesListStatus}
          loading={loading}
          getPipelinesList={getPipelinesList}
          pipelinesList={pipelinesList}
          columns={columns}
          startPollingPipelinesList={startPollingPipelinesList}
          getEmptyLabelForTable={getEmptyLabelForTable}
          actionStatus={actionStatus}
          stopPollingPipelinesList={stopPollingPipelinesList}
          sortValuePipelinesList={sortValuePipelinesList}
          resetCurrentPage={resetCurrentPage}
          totalSelectedPipelines={totalSelectedPipelines}
          onDeleteMultiplePipelines={onDeleteMultiplePipelines}
          setTotalSelectedPipelines={setTotalSelectedPipelines}
          newPipelineName={newPipelineName}
          resetNewPipelineName={resetNewPipelineName}
          activatePipelineActionStatus={activatePipelineActionStatus}
        />
      )}

      {pipelineToShare && (
        <ShareModal pipelineName={pipelineToShare} onCancel={() => setPipelineToShare('')} />
      )}
      {/* todo: remove this, is not being used? */}
      {showRenamePipelineModal && (
        <NamePipelineModal
          openModal={showRenamePipelineModal}
          closeModal={onCloseNamePipelineModal}
          modalTitle={getNamePipelineModalTitle()}
          okButtonText={
            createPipelineFromEmptyYaml
              ? NAME_NEW_PIPELINE_MODAL_OK_BUTTON
              : RENAME_PIPELINE_MODAL_OK_BUTTON
          }
          pipelineName={createPipelineFromEmptyYaml ? '' : pipelineToRename}
          description={createPipelineFromEmptyYaml ? COMMON_FORM_ERRORS.INVALID_CHARACTERS : ''}
          renamePipeline={(payload: { pipelineName: string; newPipelineName: string }) => {
            if (!createPipelineFromEmptyYaml) updatePipeline(payload);
          }}
          createPipelineFromEmptyYaml={(pipelineName: string) => {
            if (createPipelineFromEmptyYaml) {
              createPipelineFromEmptyYamlHandler(pipelineName);
            }
          }}
          onAfterPipelineCreated={onAfterPipelineCreated}
          onAfterPipelineRenamed={onAfterPipelineRenamed}
          renamePipelineNameStatus={
            createPipelineFromEmptyYaml ? undefined : renamePipelineNameStatus
          }
          pipelineCreateUpdatePipelineStatus={pipelineCreateUpdatePipelineStatus}
          resetRenamePipelineNameStatus={resetRenamePipelineNameStatus}
          pipelineMessage={message}
          resetMessage={resetMessage}
        />
      )}
      {openPipelineConfirmationModal && (
        <PipelineConfirmationModal
          open={openPipelineConfirmationModal}
          okText={pipelineToUndeploy ? UNDEPLOY_BUTTON_LABEL : DELETE_BUTTON_LABEL}
          onOk={getOnOkPipelineConfirmationModal}
          cancelText={CANCEL_BUTTON_LABEL}
          onCancel={getOnCancelPipelineConfirmationModal}
          pipelines={getPipelinesForConfirmationModal()}
          undeployingPipeline={!!pipelineToUndeploy}
        />
      )}
    </div>
  );
};

export default PipelinesList;
